import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './ApiEndpoints'

/**
 * Headers aligned with app conventions: Bearer token + institute-Id (Laravel).
 */
export function buildAnnouncementHeaders() {
  const instituteId = Cookies.get('institute_id')
  const userCookie = Cookies.get('user')
  let token = ''
  if (userCookie) {
    try {
      token = JSON.parse(userCookie)?.token || ''
    } catch {
      token = ''
    }
  }
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`
  if (instituteId) headers['institute-Id'] = Number(instituteId)
  return headers
}

export function parseAnnouncementListResponse(res) {
  const raw = res?.data
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw?.data)) return raw.data
  if (Array.isArray(raw?.data?.data)) return raw.data.data
  return []
}

export function parseAnnouncementOne(res) {
  const raw = res?.data
  if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    return raw.data
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
  return null
}

/**
 * @param {unknown} error - axios error
 * @param {import('react-router-dom').NavigateFunction} [navigate]
 * @returns {string} message for toast
 */
export function getAnnouncementErrorMessage(error, navigate) {
  const status = error?.response?.status
  const data = error?.response?.data

  if (status === 401) {
    if (typeof navigate === 'function') {
      navigate('/LoginSignup', { replace: true })
    }
    return data?.message || 'Unauthorized. Please login again.'
  }
  if (status === 403) {
    return data?.message || 'Permission denied.'
  }
  if (status === 404) {
    return data?.message || 'Not found.'
  }
  if (status === 422) {
    const errs = data?.errors
    if (errs && typeof errs === 'object') {
      const msgs = Object.values(errs)
        .flat()
        .filter(Boolean)
      if (msgs.length) return msgs.join(' ')
    }
    return data?.message || 'Validation failed.'
  }
  return (
    data?.message ||
    data?.result ||
    error?.message ||
    'Something went wrong.'
  )
}

export async function fetchAnnouncementsList(params = {}) {
  return axios.get(`${BASE_URL}/announcements`, {
    headers: buildAnnouncementHeaders(),
    params,
  })
}

export async function fetchTopAnnouncements() {
  return axios.get(`${BASE_URL}/announcements/top`, {
    headers: buildAnnouncementHeaders(),
  })
}

export async function fetchAnnouncementById(id) {
  return axios.get(`${BASE_URL}/announcements/${id}`, {
    headers: buildAnnouncementHeaders(),
  })
}

export async function createAnnouncementRequest(body) {
  return axios.post(`${BASE_URL}/announcements`, body, {
    headers: buildAnnouncementHeaders(),
  })
}

export async function uploadAnnouncementMedia(id, file) {
  const formData = new FormData()
  formData.append('media', file)
  const headers = buildAnnouncementHeaders()
  delete headers['Content-Type']
  return axios.post(`${BASE_URL}/announcements/${id}/upload-media`, formData, {
    headers,
  })
}

export async function updateAnnouncementRequest(id, body) {
  return axios.put(`${BASE_URL}/announcements/${id}`, body, {
    headers: buildAnnouncementHeaders(),
  })
}

export async function deleteAnnouncementRequest(id) {
  return axios.delete(`${BASE_URL}/announcements/${id}`, {
    headers: buildAnnouncementHeaders(),
  })
}

export function normalizeClassIdsFromApi(raw) {
  if (!raw) return []
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => {
      if (typeof x === 'object' && x !== null) {
        return Number(x.id ?? x.class_id ?? x)
      }
      return Number(x)
    })
    .filter((n) => !Number.isNaN(n))
}

export function toDatetimeLocalInput(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function datetimeLocalToIsoOrNull(value) {
  if (!value || !String(value).trim()) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}
